import { UserManager, Log } from 'oidc-client';
import queryString from 'query-string';
import config from '../config/Config';
import { ApplicationPaths, BrokerParams } from './AuthorizationConstants';
import getLogger from '../utils/logger';
import { getLocalStorageItem } from '../utils/clientStorage';

const log = getLogger();

const populateOIDCLogLevel = () => {
  const logLevel = getLocalStorageItem('loglevel:rcore') || 'ERROR';
  // Possible log levels for oidc logger
  // NONE: 0; ERROR: 1; WARN: 2; INFO: 3; DEBUG: 4;
  return Log[logLevel.toUpperCase()];
};

Log.logger = console;
Log.level = populateOIDCLogLevel();

export class AuthorizeService {
  callbacks = [];

  nextSubscriptionId = 0;

  isUserAuthenticated = false;

  loggedinUser = null;

  async isAuthenticated() {
    const user = await this.getUser();
    return !!user;
  }

  async getLoggedInUserId() {
    const user = await this.getUserProfile();
    return user && user.userid;
  }

  async getMaskedUserId() {
    const user = await this.getUserProfile();
    return user && user.maskeduserid;
  }

  async getUserProfile() {
    const user = await this.getUser();
    return user && user.profile;
  }

  async getUser() {
    if (this.loggedinUser) {
      return this.loggedinUser;
    }

    await this.ensureUserManagerInitialized();
    const user = await this.userManager.getUser();
    return user;
  }

  async getAccessToken() {
    await this.ensureUserManagerInitialized();
    const user = await this.userManager.getUser();
    return user && user.access_token;
  }

  async removeAccessToken() {
    await this.userManager.removeUser();
  }

  async signIn(state) {
    await this.ensureUserManagerInitialized();
    try {
      await this.userManager.signinRedirect(this.createArguments(state));
      return this.redirect();
    } catch (redirectError) {
      return this.error(redirectError);
    }
  }

  async completeSignIn(url) {
    try {
      await this.ensureUserManagerInitialized();
      let user = null;
      const that = this;
      return this.userManager
        .signinCallback(url)
        .then((data) => {
          user = data;
          that.updateState(user);
          return that.success(user && user.state);
        })
        .catch((error) => {
          log.error(error);
          return that.error('There is an error signing in');
        });
    } catch (error) {
      log.error(error);
      return this.error('There is an error signing in');
    }
  }

  async isEmulationMode() {
    const user = await this.getUserProfile();
    return user && user.emulatorusername && user.emulatorusername !== '';
  }

  async isGuestUser() {
    const user = await this.getUserProfile();
    return user && user.guestaccount && user.guestaccount === '1';
  }

  async signOut() {
    await this.ensureUserManagerInitialized();
    try {
      if (!this.isAuthenticated()) {
        return this.success();
      }
      await this.userManager.removeUser();

      this.updateState(null);

      window.location.href = this.signoutUrl();
      return this.redirect();
    } catch (redirectSignOutError) {
      return this.error(redirectSignOutError);
    }
  }

  updateState(user) {
    this.loggedinUser = user;
    if (user == null) {
      this.isUserAuthenticated = false;
    } else {
      this.isUserAuthenticated = !!this.loggedinUser;
    }
    this.notifySubscribers();
  }

  subscribe(callback) {
    this.callbacks.push({ callback, subscription: this.nextSubscriptionId++ });
    return this.nextSubscriptionId - 1;
  }

  unsubscribe(subscriptionId) {
    const subscriptionIndex = this.callbacks
      .map((e, i) => (e.subscription === subscriptionId ? { found: true, i } : { found: false }))
      .filter((e) => e.found === true);
    if (subscriptionIndex.length !== 1) {
      console.log(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
    } else {
      this.callbacks.splice(subscriptionIndex[0].index, 1);
    }
  }

  notifySubscribers() {
    for (let i = 0; i < this.callbacks.length; i++) {
      const { callback } = this.callbacks[i];
      callback();
    }
  }

  signoutUrl = () => `${config.domain}apisecurity/account/signout`;

  createArguments = (state) => ({ useReplaceToNavigate: true, data: state });

  error = (message) => ({ status: AuthenticationResultStatus.Fail, message });

  success = (state) => ({ status: AuthenticationResultStatus.Success, state });

  redirect = () => ({ status: AuthenticationResultStatus.Redirect });

  async ensureUserManagerInitialized() {
    if (this.userManager !== undefined) {
      return;
    }

    // Parse and add any extra parameters that need to be passed on to broker
    const brokerQueryParams = {};
    const qs = queryString.parse(window.location.search);
    BrokerParams.forEach((param) => {
      if (Object.keys(qs).includes(param)) {
        brokerQueryParams[`broker_${param}`] = qs[param];
      }
    });

    const settings = {
      authority: `${config.domain}apisecurity`,
      client_id: 'sumtotal_rcore',
      redirect_uri: `${config.spaSiteDomain}${config.appPrefix}${ApplicationPaths.LoginCallback}`,
      post_logout_redirect_uri: `${config.spaSiteDomain}${config.appPrefix}${ApplicationPaths.LogoutCallback}`,
      response_type: 'code',
      scope: 'openid profile allapis all notifications:all notifications:read notifications:create',
      extraQueryParams: brokerQueryParams,
      timeInsensitive: true,
      silent_redirect_uri: `${config.spaSiteDomain}${config.appPrefix}${ApplicationPaths.SilentRediectUri}`
    };
    settings.automaticSilentRenew = true;
    settings.includeIdTokenInSilentRenew = false;

    this.userManager = new UserManager(settings);
    window.userManager = this.userManager;
    this.userManager.events.addUserSignedOut(async () => {
      await this.signOut();
    });
    this.userManager.events.addAccessTokenExpired(async () => {
      await this.signOut();
    });
  }

  static get instance() {
    return authService;
  }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
  Redirect: 'redirect',
  Success: 'success',
  Fail: 'fail'
};
